import React from "react";
import type { SplitButtonState } from "@fluentui/react-components";
import {
  useSplitButtonStyles_unstable,
  makeStyles,
  mergeClasses,
  shorthands,
  splitButtonClassNames,
  createCustomFocusIndicatorStyle,
} from "@fluentui/react-components";

// eslint-disable-next-line no-restricted-imports
import {
  commonHighContrastRestStyles,
  commonHighContrastDisabledStyles,
  HighContrastSelector,
} from "@1js/acui-common/lib/styling/next/commonStyles.styles";

// eslint-disable-next-line no-restricted-imports
import type { AppSplitButtonState } from "@1js/acui-button/lib/Next";
import { getRibbonButtonSizedStyles } from "../../next/commonRibbonButtonStyles.styles";
import { customTokens, isNextStyleEnabled } from "@1js/acui-theme";
import { ActionControlSize } from "@1js/acui-common";
import { useMinimumTargetSizesIsEnabled } from "@1js/acui-experiments";
import { AppContext } from "@1js/acui-context";

import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";

export const useToggleableRibbonSplitButtonStylesEnabled = () => {
  const appContext = React.useContext(AppContext);
  return checkExperiment(
    appContext,
    ribbonExperiments.fixToggleableRibbonSplitButtonStylesEnabled
  );
};

const useRootStyles = makeStyles({
  base: {
    [`${HighContrastSelector}`]: {
      [`& .${splitButtonClassNames.primaryActionButton}`]: {
        ...commonHighContrastRestStyles,
      },
      [`& .${splitButtonClassNames.menuButton}`]: {
        ...commonHighContrastRestStyles,
        ":focus": {
          ...commonHighContrastRestStyles,
        },
      },
    },
    ":hover": {
      [`& .${splitButtonClassNames.primaryActionButton}`]: {
        ...shorthands.borderColor(customTokens.legacy_neutralLighter),
      },
      [`& .${splitButtonClassNames.menuButton}`]: {
        ...shorthands.borderColor(customTokens.legacy_neutralLighter),
      },
    },
    ":active": {
      [`& .${splitButtonClassNames.primaryActionButton}`]: {
        ...shorthands.borderColor(customTokens.legacy_neutralQuaternaryAlt),
      },
      [`& .${splitButtonClassNames.menuButton}`]: {
        ...shorthands.borderColor(customTokens.legacy_neutralQuaternaryAlt),
      },
    },
  },
  disabled: {
    [`${HighContrastSelector}`]: {
      [`& .${splitButtonClassNames.primaryActionButton}`]: {
        ...commonHighContrastDisabledStyles,
        ...commonHighContrastRestStyles,
      },
      ":hover": {
        [`& .${splitButtonClassNames.primaryActionButton}`]: {
          ...commonHighContrastDisabledStyles,
          ...commonHighContrastRestStyles,
        },
        [`& .${splitButtonClassNames.menuButton}`]: {
          ...commonHighContrastDisabledStyles,
          ...commonHighContrastRestStyles,
        },
      },
    },
  },
  large: {
    flexDirection: "column",
    minWidth: "40px",

    // Have to do this because of Fluent style in useSplitButtonStyles
    // Specificity is greater than a regular class name on the MenuButton
    [`& .${splitButtonClassNames.menuButton}`]: {
      borderLeftWidth: "1px",
    },
  },
});

// Have to do this because of Fluent style in useSplitButtonStyles
// Specificity is greater than a regular class name on the MenuButton
// remove when we clean up useMinimumTargetSizesIsEnabled
const useMenuButtonNoMinTargetSizeStyles = makeStyles({
  small: {
    [`& .${splitButtonClassNames.menuButton}`]: {
      width: "12px",
      minWidth: "12px",
    },
  },
  medium: {
    [`& .${splitButtonClassNames.menuButton}`]: {
      minWidth: "16px",
    },
  },
  withMinTargetSize: {},
});

const useRootStylesNext = makeStyles({
  large: {
    flexDirection: "column",
    minWidth: "40px",
    [`& .${splitButtonClassNames.primaryActionButton}`]: {
      borderBottomLeftRadius: customTokens.borderRadiusNone,
      borderBottomRightRadius: customTokens.borderRadiusNone,
      borderTopRightRadius: customTokens.borderRadiusMedium,
      borderRightWidth: customTokens.strokeWidthThin,
      borderRightColor: customTokens.colorTransparentBackground,
      flexGrow: 1,
    },
    [`& .${splitButtonClassNames.menuButton}`]: {
      borderLeftWidth: customTokens.strokeWidthThin,
      borderTopLeftRadius: customTokens.borderRadiusNone,
      borderTopRightRadius: customTokens.borderRadiusNone,
      borderBottomLeftRadius: customTokens.borderRadiusMedium,
    },
  },
  small: {
    [`& .${splitButtonClassNames.primaryActionButton}`]: {
      borderRightColor: customTokens.colorTransparentBackground,
    },
  },
});

const useLargeDisabledStyles = makeStyles({
  large: {
    ":hover:active": {
      [`& .${splitButtonClassNames.primaryActionButton}`]: {
        borderRightColor: customTokens.colorTransparentBackground,
      },
    },
    ":hover": {
      [`& .${splitButtonClassNames.primaryActionButton}`]: {
        borderRightColor: customTokens.colorTransparentBackground,
      },
    },
  },
});

const useBaseDisabledStyles = makeStyles({
  base: {
    ":hover:active": {
      [`& .${splitButtonClassNames.primaryActionButton}`]: {
        borderRightColor: customTokens.colorTransparentBackground,
      },
    },
    ":hover": {
      [`& .${splitButtonClassNames.primaryActionButton}`]: {
        borderRightColor: customTokens.colorTransparentBackground,
      },
    },
  },
});

const useFocusStyles = makeStyles({
  primaryActionButton: createCustomFocusIndicatorStyle({
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderTopRightRadius: customTokens.borderRadiusMedium,
  }),
  menuButton: createCustomFocusIndicatorStyle({
    borderTopWidth: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: customTokens.borderRadiusMedium,
  }),
});

const useFocusStylesNext = makeStyles({
  primaryActionButton: createCustomFocusIndicatorStyle({
    borderBottomLeftRadius: customTokens.borderRadiusNone,
    borderBottomRightRadius: customTokens.borderRadiusNone,
    borderTopRightRadius: customTokens.borderRadiusMedium,
  }),
  menuButton: createCustomFocusIndicatorStyle({
    borderTopWidth: 0,
    borderTopLeftRadius: customTokens.borderRadiusNone,
    borderTopRightRadius: customTokens.borderRadiusNone,
    borderLeftWidth: customTokens.strokeWidthThin,
    borderBottomLeftRadius: customTokens.borderRadiusMedium,
  }),
});

const useToggledRibbonSplitButtonStyles = makeStyles({
  base: {
    [`& .${splitButtonClassNames.primaryActionButton}`]: {
      ...shorthands.borderColor(customTokens.legacy_neutralLight),
    },
    [`& .${splitButtonClassNames.menuButton}`]: {
      ...shorthands.borderColor(customTokens.legacy_neutralLight),
    },
  },
});

const useToggledSplitStyles = makeStyles({
  base: {
    [`& .${splitButtonClassNames.primaryActionButton}`]: {
      ...shorthands.borderColor(customTokens.colorNeutralStrokeAccessible),
    },
    [`& .${splitButtonClassNames.menuButton}`]: {
      ...shorthands.borderColor(customTokens.colorNeutralStrokeAccessible),
      backgroundColor: customTokens.colorNeutralBackground1Selected,
    },
  },
});

const useNonLargeToggledStyles = makeStyles({
  base: {
    [`& .${splitButtonClassNames.primaryActionButton}`]: {
      borderRightColor: "transparent",
    },
    [`& .${splitButtonClassNames.menuButton}`]: {
      borderLeftColor: "transparent",
    },
  },
});

const useFocusHighContrastStylesNext = makeStyles({
  base: {
    [`${HighContrastSelector}`]: {
      ":focus": {
        outlineStyle: "solid",
      },
      ":focus-visible": {
        outlineStyle: "solid",
      },
    },
  },
});

export const useRibbonSplitButtonStyles = (state: AppSplitButtonState) => {
  const { toggleable, toggled } = state;

  const rootStyles = useRootStyles();
  const toggledRootStyles = useToggledRibbonSplitButtonStyles();
  const focusStyles = useFocusStyles();
  const rootStylesNext = useRootStylesNext();
  const focusStylesNext = useFocusStylesNext();
  const focusHighContrastStylesNext = useFocusHighContrastStylesNext();
  const toggledRootStylesNext = useToggledSplitStyles();

  const noMinTargetStyles = useMenuButtonNoMinTargetSizeStyles();
  const context = React.useContext(AppContext);
  const minimumTargetSizesEnabled = useMinimumTargetSizesIsEnabled(context);

  const rootStylesUpdatedNext = useLargeDisabledStyles();
  const rootStylesOnDisabled = useBaseDisabledStyles();
  const nonLargeToggledStyles = useNonLargeToggledStyles();
  const isToggleableRibbonSplitButtonStylesEnabled =
    useToggleableRibbonSplitButtonStylesEnabled();

  if (isNextStyleEnabled()) {
    state.root.className = mergeClasses(
      getRibbonButtonSizedStyles(state.size, rootStylesNext),
      toggleable && toggled && toggledRootStylesNext.base,
      !minimumTargetSizesEnabled &&
        getRibbonButtonSizedStyles(state.size, noMinTargetStyles),
      state.root.className
    );

    // Fix disabled state styles for border issue.
    if (isToggleableRibbonSplitButtonStylesEnabled) {
      state.root.className = mergeClasses(
        state.root.className,
        state.disabled && rootStylesOnDisabled.base
      );
    } else {
      // This is making the primary button have a transparent border on the right side when the split button is hovered, and it's missing the styles for other size.
      state.root.className = mergeClasses(
        state.root.className,
        getRibbonButtonSizedStyles(state.size, rootStylesUpdatedNext)
      );
    }

    // Large buttons are vertical so we need to change the default border radius
    if (
      state.menuButton &&
      state.size === ActionControlSize.Large.toLowerCase()
    ) {
      state.menuButton.className = mergeClasses(
        focusStylesNext.menuButton,
        state.menuButton.className
      );
    }

    // Large buttons are vertical so we need to change the default border radius
    if (
      state.primaryActionButton &&
      state.size === ActionControlSize.Large.toLowerCase()
    ) {
      state.primaryActionButton.className = mergeClasses(
        focusStyles.primaryActionButton,
        state.primaryActionButton.className
      );
    }

    // Small and medium toggleable split buttons need to have transparent borders in between the primary and menu buttons.
    if (state.primaryActionButton && state.menuButton) {
      if (
        !isToggleableRibbonSplitButtonStylesEnabled ||
        state.size !== ActionControlSize.Large.toLowerCase()
      ) {
        state.root.className = mergeClasses(
          state.root.className,
          nonLargeToggledStyles.base
        );
      }
    }

    // 8072142: Fix the issue where a high contrast split button wouldn't show its focus outline properly
    if (state.primaryActionButton) {
      state.primaryActionButton.className = mergeClasses(
        state.primaryActionButton.className,
        focusHighContrastStylesNext.base
      );
    }

    // 8072142: Fix the issue where a high contrast split button wouldn't show its focus outline properly
    if (state.menuButton) {
      state.menuButton.className = mergeClasses(
        state.menuButton.className,
        focusHighContrastStylesNext.base
      );
    }
  } else {
    state.root.className = mergeClasses(
      rootStyles.base,
      getRibbonButtonSizedStyles(state.size, rootStyles),
      (state.disabled || state.disabledFocusable) && rootStyles.disabled,
      toggleable && toggled && toggledRootStyles.base,
      state.root.className
    );
  }

  useSplitButtonStyles_unstable(state as SplitButtonState);

  return state;
};
